import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router';

import { Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ActionItemKeys } from '@uplift/api';
import { useCurrentuser, useCurrentuserRefetch } from '@uplift/contexts';
import { useActionItem, useIsMobile, useStore } from '@uplift/hooks';
import { Button, PhoneVerificationModal } from '@uplift/ui-v2';

import { PolicyDisclaimers } from '../../../components/crisis-hotline-footer/policy-disclaimer';
import {
  StepContainer, StepContent, StepDescription, StepHeading, StepNavigator
} from '../../components';

export type BookingSuccessProps = {
  onExit: () => void;
  intakeFormSubmissionId: string;
};

export const BookingSuccess: React.VFC<BookingSuccessProps> = props => {
  const isMobile = useIsMobile('sm');
  const { classes } = useBookingSuccessStyles();
  const [ verificationModalOpened, setVerificationModalOpened ] = useState(false);
  const clientSMS = useActionItem(ActionItemKeys.CLIENT_SMS_VERIFY);
  const me = useCurrentuser();
  const history = useHistory();
  const { authStore } = useStore();
  const { refetchUser } = useCurrentuserRefetch();
  const goTo = async (link: string) => {
    await refetchUser();
    await authStore.fetchAccessTokenByRefreshToken();

    props.onExit();
    history.push(link);
  };

  const handleSubmit = useCallback(() => {
    goTo(`/form/submission/${props.intakeFormSubmissionId}`);
  },[ props.intakeFormSubmissionId ]);

  return (
    <StepContainer>
      <StepContent className={classes.content}>
        <div className={classes.imageContainer}>
          <img
            src="https://scdn.joinuplift.co/public/application-assets/hands-purple-background.png"
            alt="Hands"
            data-testid="hands-img"
          />
        </div>
        <StepHeading className={classes.heading}>
            You reached out for help, and that’s something to be proud of
        </StepHeading>
        <StepDescription className={classes.description}>
            Answer a few questions to help your provider get to know you. The more you feel comfortable sharing, the more your provider can tailor your first session to your needs.
        </StepDescription>

        {clientSMS?.show && (
          <div className={classes.phoneVerify}>
            <Typography variant="h5" className={classes.phoneVerifyHeader}>Don’t want to miss your sessions?</Typography>
            <Button variant="outlined" color="secondary" onClick={() => setVerificationModalOpened(true)} data-testid="sign-up-for-reminders-btn">Sign up for text reminders</Button>
          </div>
        )}

        <PhoneVerificationModal
          open={verificationModalOpened}
          userId={me?.id}
          phone={clientSMS.contact?.value}
          contactId={clientSMS.contact?.contactId}
          contactName={clientSMS.contact?.contactName}
          onSubmit={handleSubmit}
          onClose={() => setVerificationModalOpened(false)}
        />
      </StepContent>

      <StepNavigator>
        <div className={classes.navigator}>
          {!isMobile && <PolicyDisclaimers />}
          <Button variant="contained" color="secondary" fullWidth={isMobile} onClick={handleSubmit} data-testid="prepare-btn">Prepare for your session</Button>
        </div>
      </StepNavigator>

    </StepContainer>
  );
};

const useBookingSuccessStyles = makeStyles({ name: 'BookingSuccess' })(theme => ({
  /**
   * wrap image in fixed height container so that it doesn't jump around when loading
   */
  imageContainer: {
    height: '215px',
    margin: '0 auto',
    width: 'fit-content'
  },
  content: {
    paddingTop: 0,
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },

  },
  description: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      textAlign: 'center',
    },
  },
  heading: {
    [theme.breakpoints.up('sm')]: {
      textAlign: 'center',
    },
  },
  phoneVerify: {
    marginTop: theme.spacing(5),
    background: theme.palette.secondary.background,
    padding: theme.spacing(3, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      maxWidth: '343px',
    }
  },
  phoneVerifyHeader: {
    fontSize: '18px',
  },
  navigator: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flex: 1,
  }
}));
